import React, { useState, useEffect } from 'react';
import {
  Box,
  Tag,
  Table,
  DropDown,
  Grid,
  Text,
  Spacer,
  Input,
  Select,
  Button,
} from '@flexisaf/flexibull2';
import { useSelector, useDispatch } from 'react-redux';
import Theme from 'utils/theme';
import { formatNumber } from 'utils/formatNumber';
import { fetchTransactionHistory } from 'redux/transaction-history/actions';
import { ColorBox } from '../styles/courses.style';
import {
  selectTransactionHistory,
  selectTransactionHistoryLoading,
  selectQueryTransactionLoading,
} from 'redux/transaction-history/selectors';
import debounce from 'lodash.debounce';
import Remita from '../../assets/images/remita.svg';
import Paystack from '../../assets/images/paystack.svg';
import Interswitch from '../../assets/images/interswitch.svg';
import Stripe from '../../assets/images/stripe.png';
import EmptyImage from 'assets/images/empty_course.svg';
import { TransactionHistoryDetails } from './modals/transactionDetailsModal';
import { QueryTransactionModal } from './modals/queryTransactionModal';
import { calculateDateOffset, calculateOffset } from 'utils/helpers';
import { selectInstitution } from 'redux/configurations/selectors';
import CanView, { Permissions } from 'components/CanView';
import { QueryMultiTransactionModal } from './modals/queryMultipleTransactions';
import {
  TransBox,
  Boxcolumn,
  EditedPagination,
  Buttonspace,
} from '../styles/Applicationgroups.style';
import ExportTransactionsModal from './modals/exportTransactions';
import LoaderWrapper from 'components/LoaderWrapper';
import { currencyTags, EMPTY_VALUE, FALL_BACK_TZ, PAGE_SIZES } from 'utils/constants';
const moment = require('moment-timezone');

const status = {
  successful: 'success',
  failed: 'failed',
  pending: 'pending',
};

const statusColors = {
  [status.successful]: '#3EB556',
  [status.failed]: '#EF341C',
  [status.pending]: '#D4A334',
};

export const statustag = (status) => (
  <Tag
    pad="0 20px"
    solid
    small
    color={statusColors[status]}
    style={{
      width: '90px',
    }}
  >
    {status.toUpperCase()}
  </Tag>
);

export const checkTransactionStatus = (stats) => {
  if (stats === status.successful) {
    return status.successful;
  }
  if (stats === status.failed) {
    return status.failed;
  }
  return status.pending;
};

export const TransactionsHistory = () => {
  const dispatch = useDispatch();
  const transactionHistory = useSelector(selectTransactionHistory);
  const transactionLoading = useSelector(selectTransactionHistoryLoading);
  const queryLoading = useSelector(selectQueryTransactionLoading);
  const [showModal, setShowModal] = useState(false);
  const [current, setCurrent] = useState(null);
  const [pageOptions, setPageOptions] = useState({
    currentPage: 1,
    pageSize: 10,
  });
  const [displayModal, setDisplayModal] = useState(false);
  const [queryMulti, setQueryMulti] = useState(false);
  const [platformGateway, setSelectedPlatformGateway] = useState(null);
  const [transactionStatus, setSelectedTransactionStatus] = useState(null);
  const [searchField, setSearchField] = useState('');
  const [transExport, setTransExport] = useState(false);
  const institution = useSelector(selectInstitution);

  const { pageSize, currentPage } = pageOptions;

  function resetPage() {
    setPageOptions({
      ...pageOptions,
      currentPage: 1,
    });
  }

  const checkPaymentPlatform = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'remita':
        return <img height="16px" src={Remita} alt="Remita Logo" />;
      case 'paystack':
        return <img height="16px" src={Paystack} alt="Paystack Logo" />;
      case 'interswitch':
        return <img height="16px" src={Interswitch} alt="Interswitch Logo" />;
      case 'stripe':
        return <img height="20px" src={Stripe} alt="Stripe Logo" />;
      default:
        return null;
    }
  };

  const fetchTransactions = ({
    page = 1,
    size,
    search,
    platform,
    transacStatus,
  }) => {
    const pageOffset = calculateOffset(page, size);
    dispatch(
      fetchTransactionHistory({
        page: pageOffset,
        size: size,
        name: search,
        platform: platform,
        status: transacStatus,
      })
    );
  };

  const handlePagination = (page) => {
    setPageOptions({
      ...pageOptions,
      currentPage: page,
    });
    fetchTransactions({
      page: page,
      size: pageSize,
      search: searchField,
      platform: platformGateway?.value,
      transacStatus: transactionStatus?.value,
    });
  };

  const handlePageSize = ({ value }) => {
    setPageOptions({
      ...pageOptions,
      pageSize: value,
    });
    fetchTransactions({
      page: currentPage,
      size: value,
      search: searchField,
      platform: platformGateway?.value,
      transacStatus: transactionStatus?.value,
    });
  };

  const filterByPlatform = (paymentPlatform) => {
    setSelectedPlatformGateway(paymentPlatform);
    fetchTransactions({
      size: pageSize,
      search: searchField,
      platform: paymentPlatform?.value,
      transacStatus: transactionStatus?.value,
    });
    resetPage();
  };

  const filterByTransactionStatus = (transactionHistoryStatus) => {
    setSelectedTransactionStatus(transactionHistoryStatus);
    fetchTransactions({
      size: pageSize,
      search: searchField,
      platform: platformGateway?.value,
      transacStatus: transactionHistoryStatus?.value,
    });
    resetPage();
  };

  const sendQuery = (query) => {
    fetchTransactions({
      size: pageSize,
      search: query,
      platform: platformGateway?.value,
      transacStatus: transactionStatus?.value,
    });
    resetPage();
  };
  const delayedQuery = debounce((q) => sendQuery(q), 1500);
  const onSearchChange = (e) => {
    setSearchField(e.target.value);
    delayedQuery(e.target.value);
  };

  useEffect(() => {
    fetchTransactions({
      page: 1,
      size: 10,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const EmptyTransactionHistory = () => (
    <Box vAlign height="calc(100vh - 200px)">
      <Box width="300px" margin="0 auto" align="center">
        <img src={EmptyImage} alt="Empty Course" />
        <Spacer />
        <Text>No Transactions history available</Text>
      </Box>
    </Box>
  );

  return (
    <>
      <TransBox>
        <Boxcolumn>
          <Box align="left" margin="0 0 -40px">
            <Grid gap="30px 10px" default="250px 180px 180px" md="1fr">
              <Box>
                <Input
                  spaceTop
                  block
                  type="search"
                  isClearable
                  label="Search"
                  value={searchField}
                  onChange={onSearchChange}
                  placeholder="search by email, transaction ref, RRR and payer's name"
                />
              </Box>
              <Box>
                <ColorBox>
                  <Select
                    value={transactionStatus}
                    simpleOptions={['Pending', 'Success', 'Failed']}
                    elementOptions={[
                      {
                        label: 'Pending',
                        iconLeft: 'saf-circle tyellow',
                      },
                      {
                        label: 'Success',
                        iconLeft: 'saf-circle tgreen',
                      },
                      {
                        label: 'Failed',
                        iconLeft: 'saf-circle tred',
                      },
                    ]}
                    onChange={(transStatus) =>
                      filterByTransactionStatus(transStatus)
                    }
                    isClearable
                    spaceTop
                    block
                    label="status"
                  />
                </ColorBox>
              </Box>
              <Box>
                <Select
                  value={platformGateway}
                  simpleOptions={['Interswitch', 'Paystack', 'Remita']}
                  onChange={(e) => filterByPlatform(e)}
                  isClearable
                  spaceTop
                  block
                  label="Platform"
                />
              </Box>
            </Grid>
          </Box>
          <Grid
            default="minmax(200px, 300px) auto"
            lr="minmax(200px, 300px) 1fr"
            md="1fr"
          >
            <Box />
            <Box align="right">
              <Button
                spaceRight="20px"
                pale
                onClick={() => setTransExport(true)}
                pad="0 20px"
              >
                Export
              </Button>
              <CanView can={[Permissions.UPDATE_PAYMENT]}>
                <Buttonspace onClick={() => setQueryMulti(true)} pad="0 20px">
                  Query Multiple Transactions
                </Buttonspace>
              </CanView>
            </Box>
          </Grid>
        </Boxcolumn>
        {!transactionLoading && transactionHistory.data.length === 0 ? (
          <EmptyTransactionHistory />
        ) : (
          <LoaderWrapper isLoading={transactionLoading}>
            <Table
              stripped
              style={{ marginBottom: '-100px', overflow: 'scroll !important' }}
            >
              <table>
                <thead>
                  <tr>
                    <th>Payer's Email</th>
                    <th>User Account</th>
                    <th>RRR</th>
                    <th>Amount</th>
                    <th>Platform</th>
                    <th>Date</th>
                    <th>Status</th>
                    <CanView can={[Permissions.READ_PAYMENT]}>
                      <th width="30px">Action</th>
                    </CanView>
                  </tr>
                </thead>
                <tbody>
                  {transactionHistory?.data.map((history) => (
                    <tr key={history.reference}>
                      <td>
                        <Text color={Theme.PrimaryColor}>{history.email}</Text>
                      </td>
                      <td>
                        {history.username ? history.username : EMPTY_VALUE}
                      </td>
                      <td>{!history.rrr ? EMPTY_VALUE : history.rrr}</td>
                      <td>{currencyTags[history?.currency || 'NGN']}{formatNumber(history.amount + history?.gatewayCharge || 0)}</td>
                      <td>
                        {checkPaymentPlatform(history.platform.toLowerCase())}
                      </td>
                      <td>
                        {moment(
                          calculateDateOffset(
                            history.createdAt,
                            institution?.timeZone
                          )
                        )
                          .tz(institution?.timeZone || FALL_BACK_TZ)
                          .format('MMMM Do YYYY')}
                      </td>

                      <td>
                        {statustag(
                          checkTransactionStatus(history.status.toLowerCase())
                        )}
                      </td>
                      <td>
                        <CanView can={[Permissions.READ_PAYMENT]}>
                          <DropDown
                            style={{ cursor: 'pointer' }}
                            label={
                              <Tag
                                color={Theme.PrimaryBorderColor}
                                solid
                                fontColor={Theme.PrimaryInputOutline}
                                height={26}
                                pad="15px 0"
                                font="10px"
                              >
                                <i
                                  style={{ fontSize: '14px' }}
                                  className="saf-vert-dots"
                                />
                              </Tag>
                            }
                            menuAlign="bottom right"
                          >
                            <li
                              onClick={() => {
                                setCurrent(history);
                                setShowModal(true);
                              }}
                            >
                              Check Status
                            </li>
                            <li
                              onClick={() => {
                                setCurrent(history);
                                setDisplayModal(true);
                              }}
                            >
                              View Details
                            </li>
                          </DropDown>
                        </CanView>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Spacer space="100px" />
            </Table>
          </LoaderWrapper>
        )}
        {transactionHistory.data.length !== 0 && (
          <Box pad="20px">
            <EditedPagination
              pageCounts={PAGE_SIZES}
              total={transactionHistory?.resultTotal}
              itemsDisplayed
              pageSize={pageSize}
              onChange={handlePagination}
              changePageSize={handlePageSize}
              current={currentPage}
            />
          </Box>
        )}
      </TransBox>

      <TransactionHistoryDetails
        current={current}
        platformCheck={checkPaymentPlatform}
        handleCurrent={setCurrent}
        handleModal={setDisplayModal}
        show={displayModal}
      />
      <QueryTransactionModal
        current={current}
        handleCurrent={setCurrent}
        show={showModal}
        handleModal={setShowModal}
        loading={queryLoading}
      />
      <QueryMultiTransactionModal
        show={queryMulti}
        handleModal={setQueryMulti}
        loading={queryLoading}
      />
      <ExportTransactionsModal
        transExport={transExport}
        setTransExport={setTransExport}
      />
    </>
  );
};
